/**
 * Session control in the console (ADR 0001, amended): what each machine reports in
 * state.ext.control, turned into marks and summaries. Pure, so it is tested without React.
 */
import type { ControlMachineState, ControlState, Id, Session, UncontrolledReason } from '@orgus/protocol';
import { uncontrolledText } from '@orgus/protocol';
import type { Tone } from '@orgus/ui';

export type ControlMark =
  | { kind: 'none' }
  | { kind: 'controlled'; launched: boolean }
  | { kind: 'uncontrolled'; why: UncontrolledReason; text: string };

/** The mark a session shows: only on machines where control is on, and only while it runs. */
export function controlMarkFor(state: ControlState | undefined, s: Pick<Session, 'id' | 'machineId' | 'status' | 'startedAt'>): ControlMark {
  const m = state?.machines[s.machineId];
  if (!m?.installed || s.status === 'ended') return { kind: 'none' };
  const r = m.sessions.find((x) => x.sessionId === s.id);
  if (r?.controlled) return { kind: 'controlled', launched: !!r.launched };
  // Not in the machine's last report yet (it reports every few seconds): judge by the start time.
  const why: UncontrolledReason = r?.why ?? ((m.installedAt ?? 0) > s.startedAt ? 'started-before' : 'no-hook-yet');
  return { kind: 'uncontrolled', why, text: uncontrolledText(why) };
}

export interface MachineControlSummary {
  label: string;
  tone: Tone;
  /** One sentence under the machine's name. */
  detail: string;
  /** Can control be turned on here (supported, reported)? */
  canTurnOn: boolean;
}

export function machineSummary(m: ControlMachineState | undefined, online: boolean): MachineControlSummary {
  if (!m) {
    return {
      label: 'No report',
      tone: 'idle',
      detail: online ? 'Its agent does not report session control. Update Orgus on this machine.' : 'Offline, and it has not reported session control yet.',
      canTurnOn: false,
    };
  }
  const live = m.sessions.length;
  const controlled = m.sessions.filter((x) => x.controlled).length;
  if (m.installed) {
    const sessions = live === 0 ? 'No live sessions.' : `${controlled} of ${live} live session${live === 1 ? '' : 's'} controlled.`;
    if (m.problem) return { label: 'On, not answering', tone: 'waiting', detail: m.problem, canTurnOn: true };
    if (!m.listening && online) return { label: 'On, not answering', tone: 'waiting', detail: 'The hooks are installed but the endpoint is not listening, so sessions use their own permission prompts.', canTurnOn: true };
    return { label: 'On', tone: 'passed', detail: `Claude Code ${m.support.version ?? ''}`.trim() + `. ${sessions}`, canTurnOn: true };
  }
  if (!m.support.ok) return { label: 'Not supported', tone: 'failed', detail: m.support.reason, canTurnOn: false };
  const watched = live === 0 ? 'No live sessions.' : `Orgus watches its ${live === 1 ? 'live session' : `${live} live sessions`} but does not control them.`;
  return { label: 'Off', tone: 'neutral', detail: `Claude Code ${m.support.version}. ${watched}`, canTurnOn: true };
}

/** Wait choices for a permission request, in seconds. */
export const WAIT_OPTIONS: { value: string; label: string }[] = [
  { value: '30', label: '30 seconds' },
  { value: '60', label: '1 minute' },
  { value: '120', label: '2 minutes' },
  { value: '300', label: '5 minutes' },
  { value: '600', label: '10 minutes' },
  { value: '1800', label: '30 minutes' },
];

export function waitLabel(seconds: number): string {
  return WAIT_OPTIONS.find((o) => Number(o.value) === seconds)?.label ?? `${seconds} seconds`;
}

/** Machines to list: every enrolled machine, plus any that reported control and is gone. */
export function machineIds(state: ControlState | undefined, enrolled: readonly Id[]): Id[] {
  return [...new Set([...enrolled, ...Object.keys(state?.machines ?? {})])];
}
